import { httpClient } from "@/app/client/httpClient";
import { applicationStore } from "@/store/application";
import { Application, SimpleApplication } from "@/types/order/Application";
import { EstimatedPoint } from "@/types/order/EstimatedPoint";
import { PageableRequest } from "@/types/PageableRequest";
import {
  formatFromUnixTimestamp,
  formatFromISOString,
  getWorkingTimeText,
  startOfToday,
  getHour,
  now,
  parseISO,
  subHours
} from "@/utils/date";

class ApplicationService {
  fetchApplications(
    query: PageableRequest<{
      created_at__gte: string;
      created_at__lte: string;
      order: number;
      worker: string;
      status__in: string;
      applied_start_work_at__gte: string;
      applied_start_work_at__lte: string;
      order__office: number;
      evaluated_by_office: boolean;
      ordering: string;
      attendance_status__in: string;
    }>
  ) {
    return httpClient.orders.applications.$get({ query });
  }

  fetchApplication(applicationId: number) {
    return httpClient.orders.applications._id(applicationId).$get();
  }

  fetchApplicationListByIds(applicationIds: number[]) {
    return httpClient.orders.applications.$get({
      query: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        id__in: applicationIds.join(",")
      }
    });
  }

  createApplication(body: {
    orderId: number;
    workTimeOptionId: number;
    orderVersion: number;
  }) {
    return httpClient.orders.applications.$post({ body });
  }

  fetchCanApply(body: {
    orderId: number;
    workTimeOptionId: number;
    orderVersion: number;
  }) {
    return httpClient.orders.applications.can_apply.$post({ body });
  }

  createCancelRequest(body: {
    cancelReason: string;
    application: number;
    cancelType: string;
    orderVersion: number;
    orderUpdatedAt: string;
  }) {
    return httpClient.orders.cancel_request.$post({
      body
    });
  }

  startWork(applicationId: number, officeCode: string) {
    return httpClient.orders.applications
      ._id(applicationId)
      .start_work.$post({ body: { officeCode } });
  }

  finishWork(applicationId: number, officeCode: string, isOvertime: boolean) {
    return httpClient.orders.applications
      ._id(applicationId)
      .finish_work.$post({ body: { officeCode, isOvertime } });
  }

  /**
   * 指定のワーカーおよび事業所の間での次の予定を取得する
   */
  fetchNextScheduledApplication(userId: string, officeId: number | undefined) {
    return this.fetchApplications({
      worker: userId,
      // eslint-disable-next-line
      status__in: "applied",
      // eslint-disable-next-line
      applied_start_work_at__gte: formatFromUnixTimestamp(
        startOfToday(),
        "yyyy-MM-dd"
      ),
      // eslint-disable-next-line
      order__office: officeId,
      ordering: "applied_start_work_at",
      limit: 1
    }).then(res => {
      if (res.results.length === 0) return null;
      return res.results[0];
    });
  }

  fetchAvailableCount() {
    return httpClient.orders.applications.available_count.$post();
  }

  tryCancelAvailableCount(applicationId: number) {
    return httpClient.orders.applications
      ._id(applicationId)
      .available_count_before_after_cancel.$get();
  }

  // Applicationに関する処理を行うメソッド群
  /**
   * 勤務時間を下記の形式で返す
   * 08:00-17:00
   */
  getFormattedWorkTimeRange(application: SimpleApplication): string {
    return `${formatFromISOString(
      application.appliedStartWorkAt,
      "HH:mm"
    )}-${formatFromISOString(application.appliedFinishWorkAt, "HH:mm")}`;
  }

  /**
   * 勤怠変更承認済みの勤務時間を下記の形式で返す
   * 08:00-17:00
   */
  getFormattedCorrectedWorkTimeRange(application: Application): string {
    if (!application.attendanceCorrection) return "";
    return `${formatFromISOString(
      application.attendanceCorrection.actualStartedWorkAt!,
      "HH:mm"
    )}-${formatFromISOString(
      application.attendanceCorrection.actualFinishedWorkAt!,
      "HH:mm"
    )}`;
  }

  /**
   * SalaryPerDayに円をつけて返す
   */
  getSalaryPerDayWithVariousAllowances(application: Application): number {
    const { appliedSalaryPerDay, variousAllowances } = application;
    return appliedSalaryPerDay + variousAllowances;
  }

  /**
   * 勤怠変更承認済みのSalaryPerDayに円をつけて返す
   */
  getCorrectedSalaryPerDayWithVariousAllowances(
    application: Application
  ): number {
    return (
      application.attendanceCorrection?.correctedSalary! +
      application.variousAllowances
    );
  }

  /**
   * 開始日付と終了日付が同じ場合には
   * 23/03/30 20:00 - 05:00
   * と返し、異なる場合には下記のように返す
   * 23/03/30 20:00 - 23/03/31 05:00
   */
  getWorkingTimeText(application: Application): string {
    const startWorkAt = application.appliedStartWorkAt;
    const finishWorkAt = application.appliedFinishWorkAt;
    return getWorkingTimeText(startWorkAt, finishWorkAt);
  }

  confirmApplicationReminder(applicationId: number) {
    return httpClient.orders.applications.confirm_reminder.$patch({
      body: {
        applicationId
      }
    });
  }

  /**
   * 獲得予定ポイントを引いてくる.
   * TODO: お祝い金廃止に伴いこの処理の呼び出し箇所は存在しなくなるので、恒久的に不要であれば削除する
   * @param applicationId
   */
  fetchEstimatedPoint(applicationId: number): Promise<EstimatedPoint> {
    return httpClient.orders.applications
      ._id(applicationId)
      .estimated_point.$get();
  }

  get canAttend() {
    const { mostRecentAppliedApplication } = applicationStore;
    if (!mostRecentAppliedApplication) {
      return false;
    }
    // 予定の開始時間の一時間前から出勤可能
    const startTime = parseISO(mostRecentAppliedApplication.appliedStartWorkAt);
    return now() >= subHours(startTime, 1);
  }

  /**
   * 前日確認モーダルを表示する必要があるapplicationを取得する
   * @returns 前日確認モーダルを表示する必要がある場合はapplicationを返す。それ以外はnullを返す
   */
  getReminderApplication(): Application | null {
    const tomorrowAppliedApplication =
      applicationStore.tomorrowAppliedApplication;
    if (
      !tomorrowAppliedApplication ||
      tomorrowAppliedApplication.isConfirmedReminder
    ) {
      return null;
    }
    return tomorrowAppliedApplication;
  }
}

export const applicationService = new ApplicationService();
